import React, { useCallback, useEffect, useState } from 'react';
import Immutable from 'immutable';

import { Urls } from 'js/src/client';
import { AuthQueryParams, AuthReasonOptions } from 'js/src/components/auth/auth-consts';
import { PasswordWithForgotLink } from 'js/src/components/auth/common/Password';
import SocialButton, { SOCIAL_BUTTON_PROVIDERS } from 'js/src/components/auth/common/SocialButton';
import { useFormFeedback } from 'js/src/components/auth/common/useFormFeedback';
import { useForceUpdate } from 'js/src/components/common/hooks/useForceUpdate';
import { Toast } from 'js/src/components/common/Toast';
import Button, { BUTTON_TYPE, BUTTON_VARIANTS } from 'js/src/components/common/ui/Button';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import TextInput, {
    AUTOCOMPLETE_VALUES,
    TEXT_INPUT_VARIANTS,
    type TypeInputChangeEvent,
} from 'js/src/components/common/ui/TextInput';
import { loginSignupOperation } from 'js/src/data/account';
import { buildGoogleAuthUrl } from 'js/src/data/account/utils';
import { isEmailValid } from 'js/src/libs/custom-utils';
import Navigation from 'js/src/libs/navigation';
import QueryString from 'js/src/libs/querystring';
import QueryStringManipulator from 'js/src/libs/querystring-manipulator';

type Props = {
    currentPath: string;
    facebookAuthUrl: string;
    googleAuthUrl: string;
    forgotPasswordUrl: string;
};

const LoginForm = React.memo(function LoginForm({
    currentPath,
    facebookAuthUrl,
    googleAuthUrl,
    forgotPasswordUrl,
}: Props) {
    const [emailVal, setInitialEmail] = useState('');
    const [passwordVal, setPasswordVal] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [formFeedback, setFormFeedback] = useFormFeedback(
        Immutable.Map({
            email: '',
            password: '',
            global: '',
        }),
    );
    const { forceUpdate } = useForceUpdate();

    useEffect(() => {
        if (formFeedback.get('global') !== '') {
            setShowEmailForm(true);
        }
    }, [formFeedback]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (QueryString[AuthQueryParams.EXISTING_ACCOUNT] === '1') {
                Toast.info(
                    'Looks like you already have a Lumen5 account. Please log in instead.',
                    undefined,
                    undefined,
                    8000,
                );
                const newUrl = QueryStringManipulator.removeQueryStringParameter(
                    window.location.href,
                    AuthQueryParams.EXISTING_ACCOUNT,
                );
                Navigation.replaceState(newUrl);
                forceUpdate();
            }
            if (QueryString[AuthQueryParams.MUST_SIGNUP] === '1') {
                Toast.info(
                    'This account does not exist. Please sign up first.',
                    undefined,
                    undefined,
                    8000,
                );
                const newUrl = QueryStringManipulator.removeQueryStringParameter(
                    window.location.href,
                    AuthQueryParams.MUST_SIGNUP,
                );
                Navigation.replaceState(newUrl);
                forceUpdate();
            }
            if (QueryString[AuthQueryParams.DIFFERENT_METHOD] === '1') {
                Toast.info(
                    'Your account is registered with a different method (facebook, google, email/password). Please log in with the correct method.',
                    undefined,
                    undefined,
                    8000,
                );
                const newUrl = QueryStringManipulator.removeQueryStringParameter(
                    window.location.href,
                    AuthQueryParams.DIFFERENT_METHOD,
                );
                Navigation.replaceState(newUrl);
                forceUpdate();
            }
            if (QueryString[AuthQueryParams.REASON] === AuthReasonOptions.AuthStateForbidden) {
                Toast.info(
                    'Your device only supports logging in with email. Please use that option, or use a different device.',
                    undefined,
                    undefined,
                    8000,
                );
                const newUrl = QueryStringManipulator.removeQueryStringParameter(
                    window.location.href,
                    AuthQueryParams.REASON,
                );
                Navigation.replaceState(newUrl);
                forceUpdate();
            }
        }, 1000);
        return () => clearTimeout(timeoutId);
    }, [forceUpdate]);

    const handleShowEmailForm = useCallback(() => {
        setShowEmailForm(true);
    }, []);

    const emailOnChangeHandler = useCallback(
        (e: TypeInputChangeEvent) => setInitialEmail(e.target.value),
        [],
    );
    const passwordOnChangeHandler = useCallback(
        (e: TypeInputChangeEvent) => setPasswordVal(e.target.value),
        [],
    );

    const facebookOnclick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href =
                facebookAuthUrl + `?${AuthQueryParams.NEXT}=${encodeURIComponent(currentPath)}`;
        },
        [facebookAuthUrl, currentPath],
    );

    const googleOnclick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = buildGoogleAuthUrl(googleAuthUrl, currentPath);
        },
        [googleAuthUrl, currentPath],
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setFormFeedback({ type: 'CLEAR_ERRORS' });

        const lowerCaseEmail = emailVal.trim().toLowerCase();

        if (!isEmailValid(lowerCaseEmail)) {
            setFormFeedback({
                type: 'SET_ERROR',
                payload: Immutable.fromJS({
                    email: 'Please provide an email.',
                }),
            });

            return false;
        }

        if (passwordVal === '') {
            setFormFeedback({
                type: 'SET_ERROR',
                payload: Immutable.fromJS({
                    password: 'Please provide a valid password.',
                }),
            });

            return false;
        }

        setIsLoggingIn(true);
        loginSignupOperation({
            email: lowerCaseEmail,
            password: passwordVal,
            is_signup: '0',
            invite_uuid: QueryString[AuthQueryParams.INVITE_UUID],
        })
            .done((response: { next?: string; invited?: string; mfa_url?: string }) => {
                // redirect via redirect callback url
                const { next = '', invited = false, mfa_url = '' } = response;
                if (mfa_url) {
                    window.location.href = mfa_url;
                } else {
                    window.location.href =
                        invited === '1'
                            ? QueryStringManipulator.updateQueryStringParameter(
                                  next,
                                  'invited',
                                  invited,
                              )
                            : next;
                }
            })
            .fail((xhr) => {
                if (xhr.getResponseHeader('cf-mitigated') === 'challenge') {
                    // Cloudflare's Web Application Firewall is challenging that this
                    // is a valid person (and not a bot). We need to reload the page
                    // to show the user the challenge page. If they pass that challenge,
                    // the next time they submit the form, it should go through.
                    window.location.reload();
                    return;
                }
                const { responseJSON = {} } = xhr;
                setFormFeedback({
                    type: 'SET_ERROR',
                    // backend messages indicate which field has errors
                    // ie. responseJSON = { 'password': 'errorMessage' }
                    payload: Immutable.fromJS(responseJSON),
                });

                setIsLoggingIn(false);
            });
    };

    const queryString = window.location.search || '';

    return (
        <div>
            <div className="mb-l5-16">
                <SocialButton
                    provider={SOCIAL_BUTTON_PROVIDERS.GOOGLE}
                    onClick={googleOnclick}
                    text="Log in with Google"
                    data-testid="google-login"
                />
            </div>
            <div className="mb-l5-16">
                <SocialButton
                    provider={SOCIAL_BUTTON_PROVIDERS.FACEBOOK}
                    onClick={facebookOnclick}
                    text="Log in with Facebook"
                    data-testid="facebook-login"
                />
            </div>
            <div className="mb-l5-16">
                <SocialButton
                    provider={SOCIAL_BUTTON_PROVIDERS.SSO}
                    href={Urls.SSO_VERIFICATION + queryString}
                    text="Single sign-on (SSO)"
                    data-testid="sso-login"
                />
            </div>
            <div className="divider text-muted my-l5-32">
                <hr />
                <span>OR</span>
                <hr />
            </div>
            {!showEmailForm ? (
                <div>
                    <div className="mb-l5-24">
                        <Button
                            variant={BUTTON_VARIANTS.OUTLINE}
                            className="d-block w-100"
                            onClick={handleShowEmailForm}
                            text="Log in with email"
                            data-testid="email-login-btn"
                        />
                    </div>
                </div>
            ) : (
                <form
                    id="login-form"
                    method="post"
                    className="form-sidebar"
                    data-testid="login-form"
                    onSubmit={handleSubmit}
                >
                    {
                        // global form errors
                        formFeedback.get('global') !== '' && (
                            <Callout msgType={CALLOUT_TYPES.ERROR}>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: formFeedback.get('global'),
                                    }}
                                ></span>
                            </Callout>
                        )
                    }

                    <div className="mb-l5-16">
                        <TextInput
                            text={emailVal}
                            labelText="Work email"
                            variant={TEXT_INPUT_VARIANTS.STANDARD}
                            autoFocus={true}
                            onChange={emailOnChangeHandler}
                            formFeedbackMessage={formFeedback.get('email')}
                            data-testid="email"
                            autocomplete={AUTOCOMPLETE_VALUES.EMAIL}
                            ignorePasswordManagers={false}
                        />
                    </div>

                    <div className="password-container mb-l5-24">
                        <PasswordWithForgotLink
                            passwordVal={passwordVal}
                            onChange={passwordOnChangeHandler}
                            forgotPasswordUrl={forgotPasswordUrl}
                            formFeedbackMessage={formFeedback.get('password')}
                        />
                    </div>

                    <div className="mb-l5-16">
                        <Button
                            className="d-block w-100"
                            type={BUTTON_TYPE.SUBMIT}
                            text={isLoggingIn ? 'Logging in...' : 'Log in'}
                            data-testid="submit-button"
                            disabled={isLoggingIn}
                        />
                    </div>
                </form>
            )}
            <div className="text-center mt-l5-32">
                <span>Need a Lumen5 account? </span>
                <a
                    href={Urls.SIGNUP + queryString}
                    className="fw-semibold text-decoration-none"
                    data-testid="signup-link"
                >
                    Sign up
                </a>
            </div>
        </div>
    );
});

export default LoginForm;
